import { FormFieldEffects } from '../../api/types';
import {
  createLinkDataMap,
  fetchChildLinkedRecords,
  fetchParentLinkedRecords,
  fetchParentSchemas, FormLink,
} from '../../lib/linked-record-helper';
import { App } from '../../hooks/use-app';

const baseUrl = '/api/v2';

// eslint-disable-next-line import/prefer-default-export
export function formsApiClient(token: string) {
  const getOptions = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-ApiToken': token,
    },
  };

  return {
    fetchRecord: async (id: string, app?: App, formLinks?: FormLink) => {
      try {
        const res = await fetch(`${baseUrl}/records/${id}.json`, getOptions);
        if (!res.ok) {
          throw Error(`${res.status} fetching record`);
        }

        const record = await res.json();
        const recordWithLinks = record.record;
        if (formLinks) {
          const linkDataMap = createLinkDataMap([recordWithLinks], formLinks);
          // @ts-ignore
          const parentSchemas = await fetchParentSchemas(formLinks, app);
          linkDataMap.recordIds = [id];
          const [to, from] = await Promise.all([
            // @ts-ignore
            fetchChildLinkedRecords(linkDataMap, app),
            // @ts-ignore
            fetchParentLinkedRecords(linkDataMap, parentSchemas, formLinks, app),
          ]);

          if (to[id]) {
            recordWithLinks.to = to[id];
          }
          if (from[id]) {
            recordWithLinks.from = from[id];
          }
        }
        return recordWithLinks;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    fetchRecords: async (id: string, options: Record<string, any>) => {
      const params = options ? `&${new URLSearchParams(options).toString()}` : '';

      try {
        const res = await fetch(
          `${baseUrl}/records.json?form_id=${id}${params}`,
          getOptions,
        );

        if (!res.ok) {
          throw Error(`${res.status} Fetching records`);
        }

        return await res.json();
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    fetchSystemApp: async (resourceId: string) => {
      try {
        const res = await fetch(
          `${baseUrl}/forms/system_apps/${resourceId}.json`,
          getOptions,
        );

        if (!res.ok) {
          throw new Error(`${res.status} fetching system app`);
        }

        return await res.json();
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    fetchForm: async (resourceId: string) => {
      try {
        const res = await fetch(
          `${baseUrl}/forms/${resourceId}.json`,
          getOptions,
        );

        if (!res.ok) {
          throw new Error(`${res.status} fetching form`);
        }

        return await res.json();
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    fetchFormMembers: async (formId: string, options?: Record<string, any>) => {
      try {
        const res = await fetch(
          `${baseUrl}/memberships.json?standard_users_only=1&with_issues_and_tasks_only=1&type=form_members&object_id=${formId}`,
          { ...getOptions, ...options },
        );
        if (!res.ok) {
          throw new Error(`${res.status} Fetching form members`);
        }

        return await res.json();
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    updateFieldEffects: async (formId: string, fieldEffects: FormFieldEffects | null) => {
      const url = `${baseUrl}/forms/${formId}/field_effects.json`;

      try {
        const res = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-ApiToken': token,
          },
          body: JSON.stringify({ field_effects: fieldEffects }),
        });

        if (!res.ok) {
          throw new Error(`${res.status} Updating form field effects`);
        }

        return res.json();
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    fetchMembershipPermissions: async (type: string, objectId: string) => {
      try {
        const res = await fetch(
          `${baseUrl}/permissions.json?type=${type}&object_id=${objectId}`,
          getOptions,
        );
        if (!res.ok) {
          throw new Error(`${res.status} Fetching membership permissions`);
        }

        return await res.json();
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };
}
